/**
 * Texas Hold'em - The most popular poker variant
 * 2 hole cards + 5 community cards, 4 betting rounds
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, HelpCircle, Users, Bot, Coins, X, History, ChevronRight, Lightbulb, GraduationCap, TrendingUp, Eye, EyeOff, Volume2, VolumeX, BarChart3, Trophy } from 'lucide-react';
import { Button } from '../../components/ui/button';
import useHoldemStore from './holdemStore.js';
import { SUITS, BOOSTY_LOGO_URL } from './pokerConstants.js';
import { sortByRank } from './pokerEngine.js';
import { 
  getCoachRecommendation, 
  getContextualTips, 
  getLearningExplanation,
  calculateWinProbability,
  calculatePotOdds
} from './pokerCoach.js';
import { loadStats, updateStats, calculateDerivedStats, resetStats, formatSessionDuration } from './sessionStats.js';
import narrator from './voiceNarrator.js';

// Chip Animation Component - Shows chips flying from player to pot
const ChipAnimation = ({ fromPosition, amount, onComplete }) => {
  const chipCount = Math.min(5, Math.max(1, Math.ceil(amount / 50))); // 1-5 chips based on amount
  
  // Calculate start position based on player seat
  const getStartPosition = () => {
    const positions = {
      0: { x: '50%', y: '85%' },      // You - bottom
      1: { x: '10%', y: '50%' },      // Left
      2: { x: '20%', y: '15%' },      // Top-left
      3: { x: '50%', y: '15%' },      // Top center
      4: { x: '80%', y: '15%' },      // Top-right
      5: { x: '90%', y: '50%' }       // Right
    };
    return positions[fromPosition] || positions[0];
  };
  
  const startPos = getStartPosition();
  
  return (
    <div className="absolute inset-0 pointer-events-none z-30 overflow-hidden">
      {Array.from({ length: chipCount }).map((_, i) => (
        <motion.div
          key={i}
          initial={{ 
            left: startPos.x, 
            top: startPos.y,
            scale: 0,
            opacity: 0
          }}
          animate={{ 
            left: '50%', 
            top: '50%',
            scale: [0, 1.2, 1, 0.8],
            opacity: [0, 1, 1, 0]
          }}
          transition={{ 
            duration: 0.6,
            delay: i * 0.08,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
          onAnimationComplete={i === chipCount - 1 ? onComplete : undefined}
          className="absolute -translate-x-1/2 -translate-y-1/2"
          style={{ 
            marginLeft: (i - chipCount/2) * 8,
            marginTop: Math.random() * 10 - 5
          }}
        >
          {/* Poker Chip */}
          <div className="w-8 h-8 rounded-full relative" style={{
            background: 'linear-gradient(145deg, #f59e0b 0%, #d97706 50%, #b45309 100%)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4), inset 0 2px 4px rgba(255,255,255,0.3)'
          }}>
            <div className="absolute inset-1 rounded-full border-2 border-dashed border-white/40" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-[8px] font-bold text-white drop-shadow-md">$</span>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

// Dramatic Card Reveal Component for Showdown
const RevealCard = ({ card, delay = 0, isWinningHand }) => {
  const [revealed, setRevealed] = useState(false);
  const suitInfo = SUITS[card?.suit];
  
  useEffect(() => {
    const timer = setTimeout(() => setRevealed(true), delay * 1000);
    return () => clearTimeout(timer);
  }, [delay]);
  
  if (!card || !suitInfo) return null;
  
  return (
    <div className="perspective-1000">
      <motion.div
        initial={{ rotateY: 180 }}
        animate={{ rotateY: revealed ? 0 : 180 }}
        transition={{ 
          duration: 0.6, 
          delay: revealed ? 0 : delay,
          type: 'spring',
          damping: 15
        }}
        className="w-12 h-16 relative preserve-3d"
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* Card Back */}
        <div 
          className="absolute inset-0 rounded-lg backface-hidden"
          style={{ 
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            background: 'linear-gradient(135deg, #7c3aed 0%, #4c1d95 50%, #1e1b4b 100%)',
            border: '2px solid #a855f7'
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <img src={BOOSTY_LOGO_URL} alt="" className="h-8 w-auto opacity-90" />
          </div>
        </div>
        
        {/* Card Front */}
        <motion.div 
          className={`absolute inset-0 rounded-lg bg-white backface-hidden ${isWinningHand ? 'ring-2 ring-green-400 shadow-[0_0_20px_rgba(34,197,94,0.6)]' : ''}`}
          style={{ 
            backfaceVisibility: 'hidden',
            border: '1px solid #cbd5e1'
          }}
          animate={isWinningHand && revealed ? {
            boxShadow: ['0 0 20px rgba(34,197,94,0.6)', '0 0 30px rgba(34,197,94,0.8)', '0 0 20px rgba(34,197,94,0.6)']
          } : {}}
          transition={{ duration: 1, repeat: Infinity }}
        >
          <div className="absolute top-0.5 left-1 flex flex-col items-center leading-none">
            <span className="text-sm font-black" style={{ color: suitInfo.color }}>{card.display}</span>
            <span className="text-xs" style={{ color: suitInfo.color }}>{suitInfo.symbol}</span>
          </div>
          <div className="absolute bottom-0.5 right-1 flex flex-col items-center leading-none rotate-180">
            <span className="text-sm font-black" style={{ color: suitInfo.color }}>{card.display}</span>
            <span className="text-xs" style={{ color: suitInfo.color }}>{suitInfo.symbol}</span>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

// Poker Card Component
const PokerCard = ({ card, faceDown, small, delay = 0, highlight }) => {
  if (faceDown) {
    return (
      <motion.div
        initial={{ opacity: 0, rotateY: 180, y: -20 }}
        animate={{ opacity: 1, rotateY: 0, y: 0 }}
        transition={{ duration: 0.3, delay, type: 'spring', damping: 15 }}
        className={`${small ? 'w-12 h-16' : 'w-16 h-24'} rounded-lg relative overflow-hidden`}
        style={{
          background: 'linear-gradient(135deg, #7c3aed 0%, #4c1d95 50%, #1e1b4b 100%)',
          border: '2px solid #a855f7',
          boxShadow: '0 2px 8px rgba(139,92,246,0.3)'
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center">
          <img src={BOOSTY_LOGO_URL} alt="" className={`${small ? 'h-8' : 'h-12'} w-auto object-contain opacity-90`} />
        </div>
      </motion.div>
    );
  }
  
  // Safety check for card data
  if (!card || !card.suit || !card.display) {
    return <div className={`${small ? 'w-12 h-16' : 'w-16 h-24'} rounded-lg bg-gray-700`} />;
  }
  
  const suitInfo = SUITS[card.suit];
  if (!suitInfo) {
    return <div className={`${small ? 'w-12 h-16' : 'w-16 h-24'} rounded-lg bg-gray-700`} />;
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, rotateY: 180, y: -20 }}
      animate={{ opacity: 1, rotateY: 0, y: 0 }}
      transition={{ duration: 0.3, delay, type: 'spring', damping: 15 }}
      className={`${small ? 'w-12 h-16' : 'w-16 h-24'} rounded-lg relative bg-white ${highlight ? 'ring-2 ring-green-400 shadow-[0_0_12px_rgba(34,197,94,0.5)]' : ''}`}
      style={{ border: '1px solid #cbd5e1', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}
    >
      <div className="absolute top-1 left-1.5 flex flex-col items-center leading-none">
        <span className={`${small ? 'text-sm' : 'text-base'} font-black`} style={{ color: suitInfo.color }}>{card.display}</span>
        <span className={`${small ? 'text-xs' : 'text-sm'}`} style={{ color: suitInfo.color }}>{suitInfo.symbol}</span>
      </div>
      <div className="absolute bottom-1 right-1.5 flex flex-col items-center leading-none rotate-180">
        <span className={`${small ? 'text-sm' : 'text-base'} font-black`} style={{ color: suitInfo.color }}>{card.display}</span>
        <span className={`${small ? 'text-xs' : 'text-sm'}`} style={{ color: suitInfo.color }}>{suitInfo.symbol}</span>
      </div>
    </motion.div>
  );
};

// Community Cards Display
const CommunityCards = ({ cards, phase }) => {
  const phases = ['flop', 'turn', 'river', 'showdown', 'result'];
  const showFlop = phases.includes(phase);
  const showTurn = ['turn', 'river', 'showdown', 'result'].includes(phase);
  const showRiver = ['river', 'showdown', 'result'].includes(phase);
  
  return (
    <div className="flex gap-2 justify-center items-center">
      {/* Flop - 3 cards */}
      {[0, 1, 2].map(i => (
        <div key={i}>
          {showFlop && cards && cards[i] ? (
            <PokerCard card={cards[i]} delay={i * 0.1} />
          ) : (
            <div className="w-16 h-24 rounded-lg border-2 border-dashed border-slate-600/50" />
          )}
        </div>
      ))}
      {/* Spacer */}
      <div className="w-2" />
      {/* Turn - 1 card */}
      <div>
        {showTurn && cards && cards[3] ? (
          <PokerCard card={cards[3]} delay={0.1} />
        ) : (
          <div className="w-16 h-24 rounded-lg border-2 border-dashed border-slate-600/50" />
        )}
      </div>
      {/* Spacer */}
      <div className="w-2" />
      {/* River - 1 card */}
      <div>
        {showRiver && cards && cards[4] ? (
          <PokerCard card={cards[4]} delay={0.1} />
        ) : (
          <div className="w-16 h-24 rounded-lg border-2 border-dashed border-slate-600/50" />
        )}
      </div>
    </div>
  );
};

// Player Seat Component  
const PlayerSeat = ({ player, isCurrentPlayer, showCards, isWinner, position, totalPlayers, isShowdown }) => {
  // Calculate position around oval table - positions must be far from center to not overlap community cards
  const getPositionStyle = () => {
    if (totalPlayers === 2) {
      // Heads up: bottom and top
      const positions = {
        0: { bottom: '70px', left: '50%', transform: 'translateX(-50%)' },
        1: { top: '60px', left: '50%', transform: 'translateX(-50%)' }
      };
      return positions[position] || positions[0];
    }
    
    // 6-max positions around the oval - keep well away from center
    const positions = {
      0: { bottom: '50px', left: '50%', transform: 'translateX(-50%)' },     // You - bottom center
      1: { top: '50%', left: '30px', transform: 'translateY(-50%)' },        // left side
      2: { top: '60px', left: '15%' },                                       // top-left
      3: { top: '60px', left: '50%', transform: 'translateX(-50%)' },        // top center
      4: { top: '60px', right: '15%' },                                      // top-right
      5: { top: '50%', right: '30px', transform: 'translateY(-50%)' }        // right side
    };
    return positions[position] || positions[0];
  };
  
  const posStyle = getPositionStyle();
  
  // Use dramatic reveal for non-human players at showdown
  const shouldUseReveal = isShowdown && !player.isHuman && !player.folded && player.holeCards.length > 0;
  
  return (
    <div className="absolute flex flex-col items-center gap-1 z-10" style={posStyle}>
      {/* Player info badge */}
      <div className={`px-2 py-1 rounded-lg text-xs ${
        isCurrentPlayer ? 'bg-amber-500/40 border-2 border-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.4)]' : 
        player.folded ? 'bg-red-900/40 border border-red-800/50' : 
        isWinner ? 'bg-green-500/40 border border-green-400' :
        'bg-slate-800/90 border border-slate-600'
      }`}>
        <div className="flex items-center gap-1.5">
          {player.isDealer && <span className="w-4 h-4 rounded-full bg-amber-500 text-[8px] font-bold flex items-center justify-center text-black">D</span>}
          {player.isSmallBlind && <span className="w-4 h-4 rounded-full bg-blue-500 text-[8px] font-bold flex items-center justify-center text-white">S</span>}
          {player.isBigBlind && <span className="w-4 h-4 rounded-full bg-purple-500 text-[8px] font-bold flex items-center justify-center text-white">B</span>}
          <span className={`font-bold ${player.folded ? 'text-red-400 line-through' : isWinner ? 'text-green-400' : 'text-white'}`}>
            {player.name}
          </span>
          {isCurrentPlayer && <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />}
        </div>
        <div className="flex items-center justify-center gap-1 text-[10px]">
          <span className="text-amber-400">{player.chips}</span>
          {player.currentBet > 0 && <span className="text-cyan-400">({player.currentBet})</span>}
          {player.isAllIn && <span className="text-red-400 font-bold">ALL-IN</span>}
        </div>
      </div>
      
      {/* Hole cards - use RevealCard for showdown */}
      {player.holeCards.length > 0 && !player.folded && (
        <div className="flex -space-x-1">
          {shouldUseReveal ? (
            // Dramatic reveal for AI players at showdown
            player.holeCards.map((card, idx) => (
              <div key={card.id} style={{ zIndex: idx }} data-testid={`player-${player.id}-card-${idx}`}>
                <RevealCard 
                  card={card} 
                  delay={position * 0.3 + idx * 0.2}
                  isWinningHand={isWinner}
                />
              </div>
            ))
          ) : (
            // Normal card display
            player.holeCards.map((card, idx) => (
              <div key={card.id} style={{ zIndex: idx }} data-testid={`player-${player.id}-card-${idx}`}>
                <PokerCard
                  card={card}
                  faceDown={!showCards && !player.isHuman}
                  small
                  delay={idx * 0.1}
                  highlight={isWinner}
                />
              </div>
            ))
          )}
        </div>
      )}
      
      {/* Hand name on showdown */}
      {showCards && player.hand && !player.folded && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: isShowdown ? position * 0.3 + 0.5 : 0 }}
          className={`px-2 py-0.5 rounded text-[10px] font-bold ${isWinner ? 'bg-green-500 text-white shadow-[0_0_10px_rgba(34,197,94,0.5)]' : 'bg-slate-700 text-slate-200'}`}
        >
          {player.hand.hand.name}
        </motion.div>
      )}
    </div>
  );
};

// Betting Controls
const BettingControls = ({ player, validActions, currentBet, minRaise, bigBlind, onAction }) => {
  const [raiseAmount, setRaiseAmount] = useState(minRaise);
  const toCall = currentBet - player.currentBet;
  
  useEffect(() => { setRaiseAmount(minRaise); }, [minRaise]);
  
  return (
    <div className="bg-slate-900/95 rounded-xl p-3 border-2 border-amber-500/50 shadow-xl w-full" data-testid="betting-controls">
      <div className="text-amber-400 text-xs font-bold mb-2 flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
        Your Action {toCall > 0 && <span className="text-slate-400">({toCall} to call)</span>}
      </div>
      
      <div className="flex flex-wrap gap-1.5 mb-2">
        {validActions.includes('fold') && (
          <Button size="sm" onClick={() => onAction('fold')} variant="outline" className="border-red-600 text-red-400 hover:bg-red-600/20 h-8 px-3 text-xs" data-testid="action-fold-btn">Fold</Button>
        )}
        {validActions.includes('check') && (
          <Button size="sm" onClick={() => onAction('check')} variant="outline" className="border-slate-600 text-slate-300 h-8 px-3 text-xs" data-testid="action-check-btn">Check</Button>
        )}
        {validActions.includes('call') && (
          <Button size="sm" onClick={() => onAction('call')} className="bg-blue-600 hover:bg-blue-500 h-8 px-3 text-xs" data-testid="action-call-btn">Call {toCall}</Button>
        )}
        {validActions.includes('all_in') && (
          <Button size="sm" onClick={() => onAction('all_in')} className="bg-red-600 hover:bg-red-500 h-8 px-3 text-xs" data-testid="action-allin-btn">All-In</Button>
        )}
      </div>
      
      {validActions.includes('raise') && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <input type="range" min={minRaise} max={Math.max(minRaise, player.chips - toCall)} value={raiseAmount}
              onChange={(e) => setRaiseAmount(parseInt(e.target.value))} className="flex-1 accent-amber-500 h-1" data-testid="raise-slider" />
            <input type="number" value={raiseAmount} onChange={(e) => setRaiseAmount(Math.max(minRaise, parseInt(e.target.value) || minRaise))}
              className="w-14 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white text-xs text-center" data-testid="raise-amount-input" />
          </div>
          <Button size="sm" onClick={() => onAction('raise', raiseAmount)} className="w-full bg-amber-600 hover:bg-amber-500 h-8 text-xs font-semibold" data-testid="action-raise-btn">
            Raise to {raiseAmount}
          </Button>
        </div>
      )}
    </div>
  );
};

// Coach Panel - AI suggestions and odds tracker
const CoachPanel = ({ state, isEnabled, learningMode, onToggleCoach, onToggleLearning }) => {
  const { 
    gamePhase, players, currentPlayerIndex, communityCards, pot, currentBet,
    showdownRevealed
  } = state;
  
  const humanPlayer = players.find(p => p.isHuman);
  const numActivePlayers = players.filter(p => !p.folded).length;
  
  // Calculate coaching data
  const coachData = useMemo(() => {
    if (!humanPlayer || !humanPlayer.holeCards || humanPlayer.holeCards.length < 2) {
      return null;
    }
    
    return getCoachRecommendation({
      holeCards: humanPlayer.holeCards,
      communityCards,
      currentHand: humanPlayer.hand,
      pot,
      currentBet,
      playerChips: humanPlayer.chips,
      playerCurrentBet: humanPlayer.currentBet,
      position: players.findIndex(p => p.isHuman),
      numActivePlayers,
      phase: gamePhase
    });
  }, [humanPlayer, communityCards, pot, currentBet, players, numActivePlayers, gamePhase]);
  
  const tips = getContextualTips(gamePhase);
  const learning = getLearningExplanation(gamePhase);
  
  const getActionColor = (action) => {
    switch (action) {
      case 'raise': return 'text-amber-400 bg-amber-500/20';
      case 'call': return 'text-blue-400 bg-blue-500/20';
      case 'check': return 'text-slate-300 bg-slate-500/20';
      case 'fold': return 'text-red-400 bg-red-500/20';
      case 'all_in': return 'text-red-400 bg-red-500/20';
      default: return 'text-slate-400 bg-slate-500/20';
    }
  };
  
  return (
    <div className="bg-slate-900/80 rounded-xl border border-slate-700 overflow-hidden">
      {/* Toggle Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/50">
        <div className="flex items-center gap-2">
          <Lightbulb className={`w-4 h-4 ${isEnabled ? 'text-amber-400' : 'text-slate-500'}`} />
          <span className="text-xs font-semibold text-white">Coach</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onToggleLearning}
            className={`p-1 rounded ${learningMode ? 'bg-purple-500/30 text-purple-400' : 'text-slate-500 hover:text-slate-300'}`}
            title="Learning Mode"
          >
            <GraduationCap className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onToggleCoach}
            className={`p-1 rounded ${isEnabled ? 'bg-amber-500/30 text-amber-400' : 'text-slate-500 hover:text-slate-300'}`}
            title="Coach Suggestions"
          >
            {isEnabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      
      {/* Learning Mode Explanation */}
      {learningMode && (
        <div className="px-3 py-2 bg-purple-500/10 border-b border-purple-500/20">
          <div className="text-[10px] font-bold text-purple-400 mb-0.5">{learning.title}</div>
          <p className="text-[10px] text-purple-300/80 leading-relaxed">{learning.content}</p>
        </div>
      )}
      
      {isEnabled && coachData && (
        <div className="p-3 space-y-3">
          {/* Recommendation */}
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Suggestion</div>
            <div className={`px-2 py-1.5 rounded-lg ${getActionColor(coachData.action)}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold uppercase">{coachData.action}</span>
                <span className="text-[10px] opacity-70">{coachData.confidence}% confident</span>
              </div>
              <p className="text-[10px] mt-0.5 opacity-80">{coachData.reason}</p>
            </div>
          </div>
          
          {/* Odds Display */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-slate-800/50 rounded-lg p-2">
              <div className="text-[10px] text-slate-500 mb-0.5">Win %</div>
              <div className="flex items-center gap-1">
                <TrendingUp className="w-3 h-3 text-green-400" />
                <span className="text-sm font-bold text-green-400">{coachData.winProb?.toFixed(0) || 0}%</span>
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-2">
              <div className="text-[10px] text-slate-500 mb-0.5">Pot Odds</div>
              <span className="text-sm font-bold text-cyan-400">
                {coachData.potOdds?.odds || '—'}:1
              </span>
            </div>
          </div>
          
          {/* Outs & Draws */}
          {coachData.draws && coachData.draws.length > 0 && (
            <div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Draws ({coachData.outs} outs)</div>
              <div className="flex flex-wrap gap-1">
                {coachData.draws.map((draw, idx) => (
                  <span key={idx} className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded text-[10px]">
                    {draw.type} ({draw.outs})
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Starting Hand Strength (preflop only) */}
          {gamePhase === 'preflop' && (
            <div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Hand Strength</div>
              <div className="w-full bg-slate-700 rounded-full h-1.5">
                <div 
                  className="h-1.5 rounded-full bg-gradient-to-r from-red-500 via-amber-500 to-green-500"
                  style={{ width: `${Math.min(100, coachData.startingStrength * 5)}%` }}
                />
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">
                {coachData.startingStrength >= 16 ? 'Premium' : 
                 coachData.startingStrength >= 12 ? 'Strong' : 
                 coachData.startingStrength >= 8 ? 'Playable' : 
                 coachData.startingStrength >= 5 ? 'Marginal' : 'Weak'}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Quick Tips */}
      {isEnabled && (
        <div className="px-3 py-2 border-t border-slate-700/50">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Tip</div>
          <p className="text-[10px] text-slate-400 italic">
            {tips[Math.floor(Math.random() * tips.length)]}
          </p>
        </div>
      )}
    </div>
  );
};

// Session Stats Panel
const SessionStatsPanel = ({ stats, onReset }) => {
  const derivedStats = calculateDerivedStats(stats);
  
  return (
    <div className="bg-slate-900/80 rounded-xl border border-slate-700 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/50">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-cyan-400" />
          <span className="text-xs font-semibold text-white">Session Stats</span>
        </div>
        <button onClick={onReset} className="text-[10px] text-slate-500 hover:text-red-400">
          Reset
        </button>
      </div>
      
      <div className="p-3 space-y-2">
        {/* Win Rate */}
        <div className="flex justify-between items-center">
          <span className="text-[10px] text-slate-500">Win Rate</span>
          <span className={`text-sm font-bold ${derivedStats.winRate >= 50 ? 'text-green-400' : 'text-red-400'}`}>
            {derivedStats.winRate}%
          </span>
        </div>
        
        {/* Hands Played */}
        <div className="flex justify-between items-center">
          <span className="text-[10px] text-slate-500">Hands</span>
          <span className="text-sm text-white">{stats.handsPlayed} ({stats.handsWon}W)</span>
        </div>
        
        {/* Net Chips */}
        <div className="flex justify-between items-center">
          <span className="text-[10px] text-slate-500">Net Chips</span>
          <span className={`text-sm font-bold ${derivedStats.netChips >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {derivedStats.netChips >= 0 ? '+' : ''}{derivedStats.netChips}
          </span>
        </div>
        
        {/* Biggest Pot */}
        {stats.biggestPot > 0 && (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-slate-500">Biggest Pot</span>
            <span className="text-sm text-amber-400">{stats.biggestPot}</span>
          </div>
        )}
        
        {/* Best Hand */}
        {stats.bestHandName && (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-slate-500">Best Hand</span>
            <span className="text-sm text-purple-400">{stats.bestHandName}</span>
          </div>
        )}
        
        {/* Streak */}
        {stats.currentStreak !== 0 && (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-slate-500">Streak</span>
            <span className={`text-sm font-bold ${stats.currentStreak > 0 ? 'text-green-400' : 'text-red-400'}`}>
              {stats.currentStreak > 0 ? `${stats.currentStreak}W` : `${Math.abs(stats.currentStreak)}L`}
            </span>
          </div>
        )}
        
        {/* Session Duration */}
        {stats.sessionStart && (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-slate-500">Session</span>
            <span className="text-sm text-slate-400">{formatSessionDuration(stats.sessionStart)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Voice Settings Panel
const VoiceSettingsPanel = ({ enabled, onToggle, volume, onVolumeChange }) => {
  return (
    <div className="flex items-center gap-2 bg-slate-800/50 rounded-lg px-2 py-1">
      <button
        onClick={onToggle}
        className={`p-1.5 rounded ${enabled ? 'bg-cyan-500/30 text-cyan-400' : 'text-slate-500 hover:text-slate-300'}`}
        title={enabled ? 'Disable Voice' : 'Enable Voice'}
      >
        {enabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
      </button>
      {enabled && (
        <input
          type="range"
          min="0"
          max="100"
          value={volume * 100}
          onChange={(e) => onVolumeChange(e.target.value / 100)}
          className="w-16 h-1 accent-cyan-500"
          title="Voice Volume"
        />
      )}
    </div>
  );
};

// Help Modal
const HelpModal = ({ onClose, gamePhase }) => {
  const tips = getContextualTips(gamePhase);
  const allTips = {
    preflop: getContextualTips('preflop'),
    flop: getContextualTips('flop'),
    turn: getContextualTips('turn'),
    river: getContextualTips('river')
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <HelpCircle className="w-6 h-6 text-amber-400" />
            Poker Help
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Current Phase Tips */}
        <div className="mb-6">
          <h3 className="text-sm font-bold text-amber-400 mb-2 uppercase">Current: {gamePhase}</h3>
          <ul className="space-y-2">
            {tips.map((tip, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                <span className="text-amber-400 mt-0.5">•</span>
                {tip}
              </li>
            ))}
          </ul>
        </div>
        
        {/* All Phase Tips */}
        <div className="space-y-4">
          {Object.entries(allTips).map(([phase, phaseTips]) => (
            phase !== gamePhase && (
              <div key={phase}>
                <h3 className="text-xs font-bold text-slate-500 mb-1 uppercase">{phase}</h3>
                <ul className="space-y-1">
                  {phaseTips.slice(0, 2).map((tip, idx) => (
                    <li key={idx} className="text-xs text-slate-400">• {tip}</li>
                  ))}
                </ul>
              </div>
            )
          ))}
        </div>
        
        {/* Hand Rankings */}
        <div className="mt-6 pt-4 border-t border-slate-700">
          <h3 className="text-sm font-bold text-white mb-3">Hand Rankings (Best to Worst)</h3>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {[
              { name: 'Royal Flush', desc: 'A-K-Q-J-10 same suit' },
              { name: 'Straight Flush', desc: '5 consecutive same suit' },
              { name: 'Four of a Kind', desc: '4 cards same rank' },
              { name: 'Full House', desc: '3 of a kind + pair' },
              { name: 'Flush', desc: '5 cards same suit' },
              { name: 'Straight', desc: '5 consecutive cards' },
              { name: 'Three of a Kind', desc: '3 cards same rank' },
              { name: 'Two Pair', desc: '2 different pairs' },
              { name: 'One Pair', desc: '2 cards same rank' },
              { name: 'High Card', desc: 'Highest card wins' }
            ].map((hand, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-slate-800/50 p-2 rounded">
                <span className="text-amber-400 font-bold w-4">{10 - idx}</span>
                <div>
                  <div className="font-semibold text-white">{hand.name}</div>
                  <div className="text-slate-400 text-[10px]">{hand.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Hand History Panel
const HandHistory = ({ history, onClose }) => {
  const [selectedHand, setSelectedHand] = useState(null);
  
  return (
    <motion.div
      initial={{ x: 300, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 300, opacity: 0 }}
      className="absolute right-0 top-0 bottom-0 w-80 bg-slate-900/98 border-l-2 border-slate-700 p-4 overflow-y-auto z-30"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <History className="w-5 h-5 text-amber-400" />
          Hand History
        </h3>
        <button onClick={onClose} className="text-slate-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>
      
      {history.length === 0 ? (
        <p className="text-slate-500 text-sm">No hands played yet</p>
      ) : (
        <div className="space-y-2">
          {history.slice().reverse().map((hand, idx) => (
            <div key={hand.id}>
              <button
                onClick={() => setSelectedHand(selectedHand?.id === hand.id ? null : hand)}
                className={`w-full text-left p-3 rounded-lg border transition-all ${
                  selectedHand?.id === hand.id ? 'bg-slate-800 border-amber-500/50' : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-white font-semibold text-sm">Hand #{history.length - idx}</span>
                  <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${selectedHand?.id === hand.id ? 'rotate-90' : ''}`} />
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  <span className="text-green-400">{hand.winner}</span> won {hand.pot}
                  {hand.winningHand && <span className="text-amber-400"> ({hand.winningHand})</span>}
                </div>
              </button>
              
              <AnimatePresence>
                {selectedHand?.id === hand.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="bg-slate-800/30 rounded-lg p-3 mt-1 border border-slate-700/50">
                      <div className="space-y-1 text-xs text-slate-400 font-mono">
                        {hand.actions.map((action, aIdx) => (
                          <div key={aIdx} className={action.startsWith('---') ? 'text-amber-400 font-bold' : ''}>
                            {action}
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

// Mode Selector
const ModeSelector = ({ onSelectMode }) => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-red-950/30 to-slate-950 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="absolute top-4 left-4 text-gray-400 hover:text-white" data-testid="back-to-dashboard-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Texas Hold'em</h1>
          <p className="text-gray-400">The world's most popular poker game</p>
        </div>
        
        <div className="space-y-4">
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => onSelectMode('ai_opponents')}
            data-testid="mode-6max-btn"
            className="w-full bg-slate-900/90 rounded-xl border border-slate-700 p-6 text-left hover:border-red-500/50 transition-all group">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-red-600/20 flex items-center justify-center group-hover:bg-red-600/30">
                <Users className="w-7 h-7 text-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-1">6-Max Table</h2>
                <p className="text-sm text-gray-400">You vs 5 AI opponents</p>
              </div>
            </div>
          </motion.button>
          
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => onSelectMode('heads_up')}
            data-testid="mode-heads-up-btn"
            className="w-full bg-slate-900/90 rounded-xl border border-slate-700 p-6 text-left hover:border-amber-500/50 transition-all group">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-amber-600/20 flex items-center justify-center group-hover:bg-amber-600/30">
                <Bot className="w-7 h-7 text-amber-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-1">Heads Up</h2>
                <p className="text-sm text-gray-400">1v1 against AI</p>
              </div>
            </div>
          </motion.button>
        </div>
        
        <div className="mt-8 bg-slate-900/50 rounded-xl border border-slate-700/50 p-4">
          <h3 className="text-sm font-semibold text-red-400 mb-2">Game Rules</h3>
          <ul className="text-sm text-gray-400 space-y-1">
            <li>• 2 hole cards dealt to each player</li>
            <li>• 5 community cards: Flop (3), Turn (1), River (1)</li>
            <li>• Best 5-card hand from 7 cards wins</li>
            <li>• Blinds: Small (5) / Big (10)</li>
          </ul>
        </div>
      </motion.div>
    </div>
  );
};

// Main Component
const TexasHoldem = () => {
  const navigate = useNavigate();
  const [showHistory, setShowHistory] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [coachEnabled, setCoachEnabled] = useState(true);
  const [learningMode, setLearningMode] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(narrator.enabled);
  const [voiceVolume, setVoiceVolume] = useState(narrator.volume);
  const [sessionStats, setSessionStats] = useState(() => loadStats());
  const [showStats, setShowStats] = useState(false);
  const [prevPhase, setPrevPhase] = useState(null);
  
  const {
    gameMode, gamePhase, players, currentPlayerIndex, communityCards, pot, currentBet, minRaise, bigBlind,
    actionLog, showdownRevealed, winners, message, handsPlayed, handsWon, handHistory, chipAnimation,
    setGameMode, startHand, playerAction, getValidActions, nextHand, resetGame, clearChipAnimation
  } = useHoldemStore();
  
  // Voice toggle handler
  const handleVoiceToggle = useCallback(() => {
    const newEnabled = !voiceEnabled;
    setVoiceEnabled(newEnabled);
    narrator.setEnabled(newEnabled);
  }, [voiceEnabled]);
  
  const handleVolumeChange = useCallback((vol) => {
    setVoiceVolume(vol);
    narrator.setVolume(vol);
  }, []);
  
  // Announce phase changes
  useEffect(() => {
    if (gamePhase !== prevPhase && gamePhase !== 'waiting') {
      narrator.announcePhase(gamePhase);
      setPrevPhase(gamePhase);
    }
  }, [gamePhase, prevPhase]);
  
  // Announce when it's player's turn and provide coach suggestion
  const humanPlayer = players.find(p => p.isHuman);
  const isHumanTurn = humanPlayer && players[currentPlayerIndex]?.isHuman && 
    ['preflop', 'flop', 'turn', 'river'].includes(gamePhase);
  
  useEffect(() => {
    if (isHumanTurn && humanPlayer && voiceEnabled && coachEnabled) {
      // Get coach recommendation for voice
      const coachData = getCoachRecommendation({
        holeCards: humanPlayer.holeCards,
        communityCards,
        currentHand: humanPlayer.hand,
        pot,
        currentBet,
        playerChips: humanPlayer.chips,
        playerCurrentBet: humanPlayer.currentBet,
        position: players.findIndex(p => p.isHuman),
        numActivePlayers: players.filter(p => !p.folded).length,
        phase: gamePhase
      });
      
      // Enrich coach data with additional context for voice
      const enrichedData = {
        ...coachData,
        phase: gamePhase,
        handName: humanPlayer.hand?.hand?.name || coachData.evaluatedHand?.hand?.name || 'High Card',
        toCall: currentBet - humanPlayer.currentBet,
        pot,
        position: players.findIndex(p => p.isHuman)
      };
      
      setTimeout(() => {
        narrator.announceCoachSuggestion(enrichedData);
      }, 500);
    }
  }, [isHumanTurn, gamePhase]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Track session stats on game result
  useEffect(() => {
    if (gamePhase === 'result' && winners.length > 0) {
      const humanWon = winners.some(w => w.isHuman);
      const winningHand = winners[0]?.hand?.hand?.name;
      
      const newStats = updateStats(sessionStats, {
        won: humanWon,
        potSize: pot,
        chipsWon: humanWon ? pot : 0,
        chipsLost: !humanWon ? (humanPlayer?.totalBet || 0) : 0,
        handName: humanPlayer?.hand?.hand?.name,
        holeCards: humanPlayer?.holeCards
      });
      
      setSessionStats(newStats);
      
      // Announce winner
      if (winners.length > 0) {
        narrator.announceWinner(winners[0].name, winningHand, pot);
      }
    }
  }, [gamePhase, winners]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Clear chip animation after it completes
  const handleChipAnimationComplete = useCallback(() => {
    clearChipAnimation();
  }, [clearChipAnimation]);
  
  // Reset session stats
  const handleResetStats = useCallback(() => {
    const freshStats = resetStats();
    setSessionStats(freshStats);
  }, []);
  
  if (!gameMode) return <ModeSelector onSelectMode={setGameMode} />;
  
  const validActions = isHumanTurn ? getValidActions() : [];
  const isShowdown = gamePhase === 'showdown' || gamePhase === 'result';
  
  // State for coach panel
  const coachState = {
    gamePhase, players, currentPlayerIndex, communityCards, pot, currentBet, showdownRevealed
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-red-950/20 to-slate-950 relative overflow-hidden">
      {/* Header */}
      <div className="bg-slate-900/80 backdrop-blur-sm border-b border-slate-700/50 px-4 py-2">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={resetGame} className="text-slate-400 hover:text-white h-8" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-lg font-bold text-white">Texas Hold'em</h1>
              <p className="text-xs text-slate-400">Blinds: 5/10 | {gamePhase.toUpperCase()}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5" data-testid="player-chips">
              <Coins className="w-4 h-4 text-amber-400" />
              <span className="font-bold text-white text-sm">{humanPlayer?.chips || 0}</span>
            </div>
            <div className="text-xs text-slate-400" data-testid="win-loss-stats">
              W/L: <span className="text-white font-bold">{handsWon}/{handsPlayed}</span>
            </div>
            
            {/* Voice Control */}
            <VoiceSettingsPanel 
              enabled={voiceEnabled}
              onToggle={handleVoiceToggle}
              volume={voiceVolume}
              onVolumeChange={handleVolumeChange}
            />
            
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowStats(!showStats)} 
              className={`h-8 ${showStats ? 'text-cyan-400' : 'text-slate-400 hover:text-white'}`}
              title="Session Stats"
            >
              <BarChart3 className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setCoachEnabled(!coachEnabled)} 
              className={`h-8 ${coachEnabled ? 'text-amber-400' : 'text-slate-400 hover:text-white'}`}
              data-testid="coach-toggle-btn"
              title="Toggle Coach"
            >
              <Lightbulb className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowHistory(true)} className="text-slate-400 hover:text-white h-8" data-testid="hand-history-toggle-btn">
              <History className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowHelp(true)} className="text-slate-400 hover:text-white h-8" data-testid="help-toggle-btn">
              <HelpCircle className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Game Table */}
      <div className="max-w-5xl mx-auto p-4">
        <div className="grid grid-cols-12 gap-3">
          {/* Action Log */}
          <div className="col-span-3">
            <div className="bg-slate-900/80 rounded-xl p-3 border border-slate-700 max-h-64 overflow-y-auto">
              <h4 className="text-xs text-slate-500 font-semibold mb-2">Action Log</h4>
              <div className="space-y-0.5">
                {actionLog.slice(-12).map((entry, idx) => (
                  <div key={idx} className={`text-xs ${entry.startsWith('---') ? 'text-amber-400 font-bold' : 'text-slate-400'}`}>
                    {entry}
                  </div>
                ))}
              </div>
            </div>
            
            {isHumanTurn && humanPlayer && (
              <div className="mt-3">
                <BettingControls
                  player={humanPlayer}
                  validActions={validActions}
                  currentBet={currentBet}
                  minRaise={minRaise}
                  bigBlind={bigBlind}
                  onAction={playerAction}
                />
              </div>
            )}
            
            {/* Coach Panel */}
            {(coachEnabled || learningMode) && (
              <div className="mt-3">
                <CoachPanel
                  state={coachState}
                  isEnabled={coachEnabled}
                  learningMode={learningMode}
                  onToggleCoach={() => setCoachEnabled(!coachEnabled)}
                  onToggleLearning={() => setLearningMode(!learningMode)}
                />
              </div>
            )}
            
            {/* Session Stats Panel */}
            {showStats && (
              <div className="mt-3">
                <SessionStatsPanel 
                  stats={sessionStats} 
                  onReset={handleResetStats}
                />
              </div>
            )}
          </div>
          
          {/* Table */}
          <div className="col-span-9">
            <div 
              className="relative rounded-[40px] border-4 border-emerald-700/60 p-4 h-[520px]"
              style={{
                background: 'radial-gradient(ellipse at center, #166534 0%, #14532d 40%, #052e16 100%)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 2px 0 rgba(255,255,255,0.03)'
              }}
            >
              {/* Message */}
              <div className="absolute top-16 left-1/2 -translate-x-1/2 z-20">
                <div className="bg-slate-900/90 px-4 py-1.5 rounded-lg border border-slate-600 text-xs">
                  <span className="text-white font-semibold">{message}</span>
                </div>
              </div>
              
              {/* Community Cards + Pot - centered with z-index to stay above seats */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-3 z-20">
                <CommunityCards cards={communityCards} phase={gamePhase} />
                {pot > 0 && (
                  <motion.div 
                    className="bg-amber-900/70 px-4 py-1.5 rounded-lg border border-amber-500/50"
                    animate={chipAnimation ? { scale: [1, 1.1, 1] } : {}}
                    transition={{ duration: 0.3 }}
                  >
                    <span className="text-amber-300 font-bold">Pot: {pot}</span>
                  </motion.div>
                )}
              </div>
              
              {/* Chip Animation Overlay */}
              <AnimatePresence>
                {chipAnimation && (
                  <ChipAnimation 
                    fromPosition={chipAnimation.fromPosition}
                    amount={chipAnimation.amount}
                    onComplete={handleChipAnimationComplete}
                  />
                )}
              </AnimatePresence>
              
              {/* Player Seats */}
              {players.map((player, idx) => (
                <PlayerSeat
                  key={player.id}
                  player={player}
                  position={idx}
                  totalPlayers={players.length}
                  isCurrentPlayer={idx === currentPlayerIndex && ['preflop', 'flop', 'turn', 'river'].includes(gamePhase)}
                  showCards={showdownRevealed || player.isHuman}
                  isWinner={winners.some(w => w.id === player.id)}
                  isShowdown={isShowdown}
                  data-testid={`player-seat-${player.id}`}
                />
              ))}
              
              {/* Deal / Next buttons */}
              {gamePhase === 'waiting' && (
                <div className="absolute bottom-32 left-1/2 -translate-x-1/2 z-30">
                  <Button onClick={startHand} className="bg-amber-600 hover:bg-amber-500 text-white font-bold px-6" data-testid="deal-btn">
                    Deal
                  </Button>
                </div>
              )}
              
              {gamePhase === 'result' && (
                <div className="absolute bottom-32 left-1/2 -translate-x-1/2 z-30">
                  <Button onClick={nextHand} className="bg-amber-600 hover:bg-amber-500 text-white font-bold px-6" data-testid="next-hand-btn">
                    Next Hand
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Hand History Panel */}
      <AnimatePresence>
        {showHistory && <HandHistory history={handHistory} onClose={() => setShowHistory(false)} />}
      </AnimatePresence>
      
      {/* Help Modal */}
      <AnimatePresence>
        {showHelp && <HelpModal onClose={() => setShowHelp(false)} gamePhase={gamePhase} />}
      </AnimatePresence>
    </div>
  );
};

export default TexasHoldem;
